import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import { be1Fetch } from '../../../../config/api';
import { useTranslation } from 'react-i18next';
import { showChoice, showConfirm } from '../../../common/AppDialog';
import { usePersistentState } from '../../../../utils/usePersistentState';
import {
  lineMarks, loadSqlCheckLog, recallResult, rememberResult, runSqlCheck, skippedCheck, sqlHash, toNodeCheck,
  type CheckLogEntry, type CheckResult, type NodeSqlCheck, type StmtResult,
} from '../../../../services/sqlCheck';
import { SqlCheckLogList, SqlCheckResultList } from './SqlCheckPane';
import { formatDateTime } from '../../../../utils/formatters';
import { pushPcConnsFor } from '../../../../services/pcConnections';

/** 검증을 쓰는 곳(SQL실행 노드)만 넘긴다 — 없으면 예전처럼 템플릿만 있는 편집기 */
export interface SqlCheckContext {
  workflowId?: string | null;
  nodeId: string;
  nodeLabel?: string;
  params?: Array<{ name: string; value: string }>;
  /** 노드에 남은 마지막 검증 요약 */
  last?: NodeSqlCheck;
}

interface Props {
  title?: string;
  value: string;
  connection?: string;
  /** sqlCheck — 적용하면서 검증했거나 「검증 없이」를 골랐을 때 노드에 남길 요약 */
  onSave: (sql: string, sqlCheck?: NodeSqlCheck) => void;
  onClose: () => void;
  check?: SqlCheckContext;
  /** 열자마자 검증 결과를 보여 줄 때(노드 패널의 「검증」·저장 때 「고치기」) */
  initialPane?: Pane;
  autoCheck?: boolean;
}

type Pane = 'templates' | 'results' | 'log';

// 이름·설명은 번역 키(property:sqlTpl.<id>.name/desc) — SQL 본문의 한글('값')은 사용자가 고칠 예시 값이라 그대로 둔다
interface Template { id: string; sql: string }
interface Group { group: string; items: Template[] }

/** 템플릿 — {{TABLE}} {{COLS}} {{KEY}} {{SRC}} 는 우측 상단 입력값으로 치환된다 */
const TEMPLATES: Group[] = [
  { group: 'query', items: [
    { id: 't01', sql: 'SELECT *\nFROM {{TABLE}};' },
    { id: 't02', sql: "SELECT {{COLS}}\nFROM {{TABLE}}\nWHERE {{KEY}} = '값'\nORDER BY {{KEY}};" },
    { id: 't03', sql: 'SELECT COUNT(*) AS cnt\nFROM {{TABLE}};' },
    { id: 't04', sql: 'SELECT {{KEY}}, COUNT(*) AS cnt, SUM(amount) AS total\nFROM {{TABLE}}\nGROUP BY {{KEY}}\nORDER BY total DESC;' },
    { id: 't05', sql: 'SELECT {{KEY}}, COUNT(*) AS cnt\nFROM {{TABLE}}\nGROUP BY {{KEY}}\nHAVING COUNT(*) > 1;' },
    { id: 't06', sql: "SELECT {{COLS}}\nFROM {{TABLE}}\nWHERE order_dt BETWEEN '${FROM_DT}' AND '${TO_DT}';" },
  ] },
  { group: 'dml', items: [
    { id: 't07', sql: 'INSERT INTO {{TABLE}} ({{COLS}})\nSELECT {{COLS}}\nFROM {{SRC}}\nWHERE 1 = 1;' },
    { id: 't08', sql: "INSERT INTO {{TABLE}} ({{COLS}})\nVALUES ('값1', '값2');" },
    { id: 't09', sql: "UPDATE {{TABLE}}\nSET col = '값'\nWHERE {{KEY}} = '값';" },
    { id: 't10', sql: "DELETE FROM {{TABLE}}\nWHERE {{KEY}} = '값';" },
    { id: 't11', sql: 'DELETE FROM {{TABLE}};\nINSERT INTO {{TABLE}} ({{COLS}})\nSELECT {{COLS}} FROM {{SRC}};\nSELECT COUNT(*) AS loaded FROM {{TABLE}};' },
    { id: 't12', sql: 'INSERT INTO {{TABLE}} ({{COLS}})\nSELECT {{COLS}} FROM {{SRC}}\nON CONFLICT ({{KEY}}) DO UPDATE SET\n  col = EXCLUDED.col;' },
    { id: 't13', sql: 'MERGE INTO {{TABLE}} t\nUSING (SELECT {{COLS}} FROM {{SRC}}) s\nON (t.{{KEY}} = s.{{KEY}})\nWHEN MATCHED THEN UPDATE SET t.col = s.col\nWHEN NOT MATCHED THEN INSERT ({{COLS}}) VALUES ({{COLS}});' },
    { id: 't14', sql: 'TRUNCATE TABLE {{TABLE}};' },
  ] },
  { group: 'ddl', items: [
    { id: 't15', sql: 'CREATE TABLE {{TABLE}} (\n  id        integer PRIMARY KEY,\n  name      varchar(100),\n  amount    numeric(15,2),\n  created_at timestamp DEFAULT now()\n);' },
    { id: 't16', sql: 'CREATE TABLE {{TABLE}} AS\nSELECT {{COLS}}\nFROM {{SRC}}\nWHERE 1 = 0;' },
    { id: 't17', sql: 'CREATE TABLE IF NOT EXISTS {{TABLE}} (\n  id integer PRIMARY KEY,\n  name varchar(100)\n);' },
    { id: 't18', sql: 'ALTER TABLE {{TABLE}} ADD COLUMN new_col varchar(50);' },
    { id: 't19', sql: 'CREATE INDEX idx_{{TABLE}}_{{KEY}} ON {{TABLE}} ({{KEY}});' },
    { id: 't20', sql: 'DROP TABLE IF EXISTS {{TABLE}};' },
    { id: 't21', sql: 'DROP TABLE IF EXISTS {{TABLE}}_stg;\nCREATE TABLE {{TABLE}}_stg AS SELECT * FROM {{TABLE}} WHERE 1 = 0;' },
  ] },
  { group: 'ops', items: [
    { id: 't22', sql: 'SELECT (SELECT COUNT(*) FROM {{SRC}}) AS src_cnt,\n       (SELECT COUNT(*) FROM {{TABLE}}) AS tgt_cnt;' },
    { id: 't23', sql: 'SELECT s.{{KEY}}\nFROM {{SRC}} s\nLEFT JOIN {{TABLE}} t ON t.{{KEY}} = s.{{KEY}}\nWHERE t.{{KEY}} IS NULL;' },
    { id: 't24', sql: 'SELECT COUNT(*) AS null_cnt\nFROM {{TABLE}}\nWHERE {{KEY}} IS NULL;' },
    { id: 't25', sql: 'ANALYZE {{TABLE}};' },
    { id: 't26', sql: "INSERT INTO etl_log (job_name, run_at, note)\nVALUES ('{{TABLE}}', now(), 'run by OpenFlow ${ENV_NAME}');" },
  ] },
];

/** 편집기 한 줄 높이(px) — textarea 의 leading-5 와 같아야 줄 번호·선택 위치가 맞는다 */
const LINE_PX = 20;
const MARK_CLASS: Record<string, string> = {
  error: 'bg-red-200 text-red-800 font-semibold',
  warn: 'bg-amber-100 text-amber-800',
  ok: 'text-green-600',
  skipped: 'text-gray-400',
};

/** SQL 편집 팝업 — 왼쪽 편집기, 오른쪽 템플릿 목록(클릭하면 커서 위치에 삽입) + 전역변수 삽입 · SQL실행 노드는 검증 */
const SqlEditorModal: React.FC<Props> = ({ title, value, connection, onSave, onClose, check, initialPane, autoCheck }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const [sql, setSql] = useState(value || '');
  const [table, setTable] = useState('');
  const [src, setSrc] = useState('');
  const [cols, setCols] = useState('*');
  const [key, setKey] = useState('id');
  const [q, setQ] = useState('');
  const [vars, setVars] = useState<string[]>([]);
  const ta = useRef<HTMLTextAreaElement>(null);
  const gutter = useRef<HTMLDivElement>(null);
  useEffect(() => { be1Fetch('/api/variables').then((r) => r.json()).then((j) => setVars((j.items || []).map((i: any) => i.name))).catch(() => {}); }, []);
  useEffect(() => { ta.current?.focus(); }, []);

  // ── 검증 — 결과는 노드마다 기억해 두고(닫았다 열어도 그대로), 고른 탭도 남긴다
  const nodeKey = check?.nodeId || '_';
  const [pane, setPane] = usePersistentState<Pane>(`sqlcheck:${nodeKey}:pane`, 'templates');
  const [result, setResult] = useState<{ hash: string; result: CheckResult } | undefined>(() => (check ? recallResult(check.nodeId) : undefined));
  const [running, setRunning] = useState(false);
  const [failMsg, setFailMsg] = useState('');
  const [logItems, setLogItems] = useState<CheckLogEntry[] | null>(null);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState('');
  const [viewing, setViewing] = useState<CheckLogEntry | null>(null);
  const curHash = sqlHash(connection, sql);

  const loadLog = useCallback(() => {
    if (!check) return;
    setLogLoading(true); setLogError('');
    loadSqlCheckLog(check.workflowId, check.nodeId)
      .then(setLogItems)
      .catch((e) => setLogError((e as Error).message || String(e)))
      .finally(() => setLogLoading(false));
  }, [check?.workflowId, check?.nodeId]);

  /** 검증을 돌리고 결과를 보여 준다 — 실패하면 r 없이 사유(err) */
  const runCheck = useCallback(async (trigger: 'manual' | 'apply'): Promise<{ r: CheckResult | null; err: string }> => {
    if (!check || !connection) { const err = t('property:sqlCheck.noConnection'); setFailMsg(err); setPane('results'); return { r: null, err }; }
    setRunning(true); setFailMsg(''); setViewing(null); setPane('results');
    try {
      await pushPcConnsFor([connection]);   // 「내 PC」 연결이면 값을 서버로 (서버가 다시 떴으면 비어 있다)
      const r = await runSqlCheck({ connection, sql, params: check.params, workflowId: check.workflowId, nodeId: check.nodeId, nodeLabel: check.nodeLabel, trigger });
      rememberResult(check.nodeId, connection, sql, r);
      setResult({ hash: sqlHash(connection, sql), result: r });
      setLogItems(null);
      return { r, err: '' };
    } catch (e) {
      const err = (e as Error).message || String(e);
      setFailMsg(err);
      setLogItems(null);
      return { r: null, err };
    } finally {
      setRunning(false);
    }
  }, [check, connection, sql, t, setPane]);

  useEffect(() => { if (initialPane) setPane(initialPane); if (autoCheck) runCheck('manual'); /* 열 때 한 번 */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (pane === 'log' && logItems === null && !logLoading) loadLog(); }, [pane, logItems, logLoading, loadLog]);

  /** 문장을 편집기에서 고른다 — 오류 줄이 있으면 그 줄로 */
  const selectStmt = (s: StmtResult) => {
    const el = ta.current;
    if (!el) return;
    const lines = sql.split('\n');
    const from = Math.max(0, Math.min(s.line - 1, lines.length - 1));
    const to = Math.max(from, Math.min(s.endLine - 1, lines.length - 1));
    const start = lines.slice(0, from).reduce((n, l) => n + l.length + 1, 0);
    const end = lines.slice(0, to + 1).reduce((n, l) => n + l.length + 1, 0) - 1;
    el.focus();
    el.setSelectionRange(start, Math.max(start, end));
    const target = ((s.errorLine || s.line) - 1) * LINE_PX;
    el.scrollTop = Math.max(0, target - el.clientHeight / 3);
  };

  // 지금 보이는 결과 — 기록을 골랐으면 그 기록
  const shown = viewing || result?.result;
  const stale = !viewing && !!result && result.hash !== curHash;
  const marks = useMemo(() => (shown && !stale && !viewing ? lineMarks(shown.statements || []) : new Map()), [shown, stale, viewing]);

  /** 적용 — SQL 을 바꿨고 아직 검증하지 않았으면 검증할지 묻는다 */
  const apply = async () => {
    const changed = sql !== (value || '');
    if (!check || !connection || !sql.trim() || !changed) { onSave(sql); return; }
    if (result && result.hash === curHash) { onSave(sql, toNodeCheck(connection, sql, result.result)); return; }
    if (check.last?.hash === curHash) { onSave(sql); return; }
    const pick = await showChoice(t('property:sqlCheck.apply.ask'), {
      title: t('property:sqlCheck.save.title'), okText: t('property:sqlCheck.apply.checkFirst'), altText: t('property:sqlCheck.apply.skip'),
    });
    if (pick === null) return;
    if (pick === 'alt') { onSave(sql, skippedCheck(connection, sql)); return; }
    const { r, err } = await runCheck('apply');
    if (!r) {
      if (await showConfirm(t('property:sqlCheck.apply.failed', { message: err }), { okText: t('property:sqlCheck.apply.skip'), cancelText: t('property:sqlCheck.apply.fix') })) {
        onSave(sql, skippedCheck(connection, sql));
      }
      return;
    }
    if (r.summary.error || r.summary.warn) {
      const ok = await showConfirm(t('property:sqlCheck.apply.issues', { error: r.summary.error, warn: r.summary.warn }), {
        title: t('property:sqlCheck.save.title'), okText: t('property:sqlCheck.apply.anyway'), cancelText: t('property:sqlCheck.apply.fix'),
      });
      if (!ok) return;
    }
    onSave(sql, toNodeCheck(connection, sql, r));
  };

  const fill = (text: string) => text.replace(/\{\{TABLE\}\}/g, table || 'table_name').replace(/\{\{SRC\}\}/g, src || 'source_table').replace(/\{\{COLS\}\}/g, cols || '*').replace(/\{\{KEY\}\}/g, key || 'id');
  const insert = (text: string) => {
    const el = ta.current;
    if (!el) { setSql((s) => (s ? s + '\n' + text : text)); return; }
    const start = el.selectionStart ?? sql.length, end = el.selectionEnd ?? sql.length;
    const before = sql.slice(0, start), after = sql.slice(end);
    const sep = before && !before.endsWith('\n') ? '\n' : '';
    const next = before + sep + text + after;
    setSql(next);
    requestAnimationFrame(() => { el.focus(); const pos = (before + sep + text).length; el.setSelectionRange(pos, pos); });
  };
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return TEMPLATES;
    return TEMPLATES.map((g) => ({ ...g, items: g.items.filter((tpl) => `${t(`property:sqlTpl.${tpl.id}.name`)} ${t(`property:sqlTpl.${tpl.id}.desc`)} ${tpl.sql}`.toLowerCase().includes(needle)) })).filter((g) => g.items.length);
  }, [q, t]);
  const lines = sql.split('\n').length;
  const inp = 'border border-gray-300 rounded px-1.5 py-0.5 text-xs bg-white font-mono';
  const activePane: Pane = check ? pane : 'templates';

  const templatesPane = (
    <>
      <div className="px-3 py-2 border-b border-gray-200">
        {!check && <div className="text-xs font-semibold text-gray-700 mb-1">{t('property:sql.templates')}</div>}
        <input className={`${inp} w-full mb-1.5`} value={q} placeholder={t('property:sql.searchTemplates')} onChange={(e) => setQ(e.target.value)} />
        <div className="grid grid-cols-[52px_1fr] gap-x-1 gap-y-1 text-[0.6875rem] text-gray-600 items-center">
          <span>TABLE</span><input className={inp} value={table} placeholder={t('property:sql.targetTable')} onChange={(e) => setTable(e.target.value)} />
          <span>SRC</span><input className={inp} value={src} placeholder={t('property:sql.sourceTable')} onChange={(e) => setSrc(e.target.value)} />
          <span>COLS</span><input className={inp} value={cols} placeholder="col1, col2" onChange={(e) => setCols(e.target.value)} />
          <span>KEY</span><input className={inp} value={key} placeholder={t('property:sql.keyColumn')} onChange={(e) => setKey(e.target.value)} />
        </div>
        <div className="text-[0.625rem] text-gray-400 mt-1">{t('property:sql.tokenHelp', { tokens: '{{TABLE}} {{SRC}} {{COLS}} {{KEY}}' })}</div>
      </div>
      <div className="flex-1 min-h-0 overflow-auto px-2 py-1.5">
        {groups.map((g) => (
          <div key={g.group} className="mb-2">
            <div className="px-1 py-0.5 text-[0.625rem] uppercase tracking-wide text-gray-400">{t(`property:sqlGroup.${g.group}`)}</div>
            {g.items.map((tpl) => (
              <button key={tpl.id} type="button" className="w-full text-left px-2 py-1 rounded hover:bg-blue-50 group" onClick={() => insert(fill(tpl.sql))} title={fill(tpl.sql)}>
                <div className="text-xs text-gray-800 group-hover:text-blue-700">{t(`property:sqlTpl.${tpl.id}.name`)}</div>
                <div className="text-[0.625rem] text-gray-400 truncate">{t(`property:sqlTpl.${tpl.id}.desc`)}</div>
              </button>
            ))}
          </div>
        ))}
        {groups.length === 0 && <div className="text-xs text-gray-400 px-2 py-4">{t('property:sql.noResults')}</div>}
      </div>
    </>
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-40" onMouseDown={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-[1180px] max-w-[96vw] h-[80vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{title || t('property:sql.defaultTitle')}{connection ? <span className="ml-2 text-xs font-normal text-gray-500">{t('property:sql.connection', { name: connection })}</span> : null}</div>
            <div className="text-[0.6875rem] text-gray-500">{t('property:sql.hint')}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('property:sql.closeEsc')}>✕</button>
        </div>
        <div className={`flex-1 min-h-0 grid ${check ? 'grid-cols-[1fr_380px]' : 'grid-cols-[1fr_320px]'} divide-x divide-gray-200`}>
          {/* 편집기 */}
          <div className="flex flex-col min-h-0">
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-100 text-[0.6875rem] text-gray-500">
              <span>{t('property:sql.stats', { lines, chars: sql.length })}</span>
              <span className="mx-1">|</span>
              <span>{t('property:sql.insertVar')}</span>
              {vars.length ? vars.map((v) => <button key={v} className="px-1.5 py-0.5 rounded border border-gray-300 hover:bg-gray-100 font-mono text-gray-700" onClick={() => insert(`\${${v}}`)}>${'{'}{v}{'}'}</button>) : <span className="text-gray-400">{t('property:sql.none')}</span>}
              <button className="ml-auto px-1.5 py-0.5 rounded border border-gray-300 hover:bg-gray-100 text-gray-700" onClick={() => setSql(sql.split('\n').map((l) => l.replace(/\s+$/, '')).join('\n').replace(/\n{3,}/g, '\n\n').trim() + (sql.trim().endsWith(';') ? '' : ''))} title={t('property:sql.tidyTitle')}>{t('property:sql.tidy')}</button>
              <button className="px-1.5 py-0.5 rounded border border-gray-300 hover:bg-gray-100 text-gray-700" onClick={() => setSql('')} title={t('property:sql.clearTitle')}>{t('property:sql.clear')}</button>
            </div>
            <div className="flex-1 min-h-0 flex">
              {/* 줄 번호 — 검증 결과가 있으면 문장 시작 줄·오류 줄에 색을 칠한다. 편집기와 함께 스크롤한다 */}
              <div ref={gutter} className="select-none m-0 py-2 pl-2 pr-1 text-right text-[0.75rem] leading-5 font-mono text-gray-400 bg-gray-50 border-r border-gray-100 overflow-hidden">
                {Array.from({ length: lines }, (_, i) => {
                  const m = marks.get(i + 1);
                  return <div key={i} className={m ? `${MARK_CLASS[m]} rounded-sm px-0.5` : 'px-0.5'}>{i + 1}</div>;
                })}
              </div>
              <textarea ref={ta} className="flex-1 min-h-0 resize-none m-0 p-2 text-[0.75rem] leading-5 font-mono outline-none bg-white text-gray-900 whitespace-pre" spellCheck={false} value={sql} onChange={(e) => setSql(e.target.value)}
                onScroll={(e) => { if (gutter.current) gutter.current.scrollTop = (e.target as HTMLTextAreaElement).scrollTop; }}
                onKeyDown={(e) => { if (e.key === 'Tab') { e.preventDefault(); insert('  '); } }} placeholder={t('property:sql.placeholder')} />
            </div>
          </div>
          {/* 오른쪽 — 템플릿 · 검증 결과 · 검증 기록 */}
          <div className="flex flex-col min-h-0 bg-gray-50">
            {check && (
              <div className="flex border-b border-gray-200 text-xs">
                {(['templates', 'results', 'log'] as Pane[]).map((p) => (
                  <button key={p} type="button" onClick={() => setPane(p)}
                    className={`flex-1 px-2 py-1.5 border-b-2 ${activePane === p ? 'border-blue-600 text-blue-700 font-semibold bg-white' : 'border-transparent text-gray-600 hover:bg-gray-100'}`}>
                    {t(`property:sqlCheck.pane.${p}`)}
                    {p === 'results' && result && !stale && (result.result.summary.error > 0
                      ? <span className="ml-1 text-red-600">●</span> : result.result.summary.warn > 0 ? <span className="ml-1 text-amber-500">●</span> : null)}
                  </button>
                ))}
              </div>
            )}
            {activePane === 'templates' && templatesPane}
            {activePane === 'results' && (
              <div className="flex-1 min-h-0 overflow-auto px-2 py-2">
                {running && <div className="text-xs text-gray-500 py-2">{t('property:sqlCheck.running')}</div>}
                {!running && failMsg && <div className="text-xs text-red-700 py-1 whitespace-pre-wrap break-words">{t('property:sqlCheck.failed', { message: failMsg })}</div>}
                {!running && viewing && (
                  <div className="flex items-center gap-2 mb-1.5 text-[0.6875rem] text-gray-600 bg-blue-50 border border-blue-100 rounded px-1.5 py-1">
                    <span className="flex-1">{t('property:sqlCheck.log.viewing', { at: formatDateTime(viewing.at) })}</span>
                    <button type="button" className="text-blue-600 hover:underline" onClick={() => { setViewing(null); setPane('log'); }}>{t('property:sqlCheck.log.back')}</button>
                    {result && <button type="button" className="text-blue-600 hover:underline" onClick={() => setViewing(null)}>{t('property:sqlCheck.log.current')}</button>}
                  </div>
                )}
                {!running && shown && <SqlCheckResultList result={shown as any} stale={stale} onSelect={viewing ? undefined : selectStmt} />}
                {!running && !shown && !failMsg && <div className="text-xs text-gray-400 py-2">{t('property:sqlCheck.notYet')}</div>}
              </div>
            )}
            {activePane === 'log' && (
              <div className="flex-1 min-h-0 overflow-auto px-2 py-2">
                <SqlCheckLogList items={logItems} loading={logLoading} error={logError} onRefresh={loadLog}
                  onPick={(e) => { setViewing(e); setPane('results'); }} />
              </div>
            )}
          </div>
        </div>
        <div className="px-4 py-2 border-t border-gray-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {check && (
              <button type="button" disabled={running || !connection} onClick={() => runCheck('manual')} title={connection ? t('property:sqlCheck.buttonTitle') : t('property:sqlCheck.noConnection')}
                className="px-3 py-1 text-xs rounded border border-blue-600 text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50">
                {running ? t('property:sqlCheck.running') : t('property:sqlCheck.button')}
              </button>
            )}
            <span className="text-[0.6875rem] text-gray-400 truncate">{t('property:sql.footer')}</span>
          </div>
          <div className="flex gap-2">
            <button className="px-3 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300 text-gray-700" onClick={onClose}>{t('property:sql.cancel')}</button>
            <button className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50" disabled={running} onClick={apply}>{t('property:sql.apply')}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SqlEditorModal;
